// hooks/useCaptions.ts
import { useState, useCallback } from "react";
import { captionService } from "../services/captionService";
import { useAppStore } from "@/core/store/useAppStore";

export interface UseCaptionsReturn {
  generateCaptions: (clientUuid: string) => Promise<void>;
  generateCaptionForPost: (postId: string, clientUuid: string) => Promise<void>; // Changed from image to post
  isGeneratingAll: boolean;
  error: string | null;
  clearError: () => void;
}

export const useCaptions = (): UseCaptionsReturn => {
  const [isGeneratingAll, setIsGeneratingAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { setPostCaption, setPostCaptionLoading } = useAppStore(); // Changed from image to post

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const generateCaptions = useCallback(async (clientUuid: string) => {
    setIsGeneratingAll(true);
    setError(null);

    try {
      await captionService.generateCaptions(clientUuid);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to generate captions";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsGeneratingAll(false);
    }
  }, []);

  const generateCaptionForPost = useCallback(
    async (postId: string, clientUuid: string) => {
      setPostCaptionLoading(postId, true);
      setError(null);

      try {
        // Call your single post caption generation
        const result = await captionService.generateCaptionForPost(
          clientUuid,
          postId,
        );

        if (result.success) {
          setPostCaption(postId, result.caption);
        } else {
          throw new Error("Caption generation failed");
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to generate caption";
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setPostCaptionLoading(postId, false);
      }
    },
    [setPostCaption, setPostCaptionLoading],
  );

  return {
    generateCaptions,
    generateCaptionForPost,
    isGeneratingAll,
    error,
    clearError,
  };
};
